import * as React from 'react';
import { dateFromPartial, exists, isPartialDate, partialDateFromDate, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { fm } from 'Components/FormatMessage';
import { defaultPapTreatmentResponseUnits } from '../../../config';
import { StepperHeaderValuePair } from '../../../../../../components/EventStepper/components';
import { mappedTreatmentResponseFields } from '../config';
import EventViewer from 'Components/EventViewer';
import { Container, Item } from 'Components/Grid';
import { aggregateResmedDataByPeriod } from 'Components/DashboardGraph/Settings/DataConversion/sleepApnea';
import { useIntl } from 'react-intl';
import InputHandler from 'Components/InputHandler';
import colors from '../../../../../../config/theme/colors';
import { Theme, useMediaQuery } from '@mui/material';

// Fields that are shown in the history
const configFields = Object.values(mappedTreatmentResponseFields);
configFields.splice(configFields.indexOf('maskLeakage95PercentilePerSecond'), 0, 'maskLeakage95PercentilePerMinute');
const resMedFields: Array<keyof ITableData> = [
  'patientHoursOfUsePerDay' /**, 'daysWithLessThan4HoursOfUse', 'daysWithLessThan4HoursOfUsePercentage'*/,
].concat(configFields) as Array<keyof IPapTreatmentResponse>;

type TAggregatedTreatmentResponseDataPoints = Array<{
  field: keyof IPapTreatmentResponse;
  value: string | number;
  date: Date;
  endDate?: Date;
  alternateDate?: string;
  description?: Array<{
    title?: string | React.JSX.Element;
    values?: any;
  }>;
}>;

interface ITableData extends IPapTreatmentResponse {
  endDate?: PartialDate;
  usageDays?: string;
}

// Compile aggregated data array to something resempling Treatment Response data array
const makeTableTimeframeData = (data: TAggregatedTreatmentResponseDataPoints): Array<Partial<ITableData>> => {
  // Get unique dates from data
  const dates = data
    .filter((dat, i, self) => self.findIndex(d => d.date.getTime() === dat.date.getTime()) === i)
    .map(uniq => ({
      date: uniq.date,
      endDate: uniq.endDate,
    }));
  // Construct Treatment responseish data from dates and datapoints
  const trData = dates.map(datemap => {
    const currentDateData = data.filter(d => d.date.getTime() === datemap.date.getTime());
    const getValue = (field: string) => currentDateData.find(cdd => cdd.field === field)?.value;
    const dataObj = resMedFields.reduce((a, v) => ({ ...a, [v]: getValue(v) }), {});
    return {
      ...{
        date: partialDateFromDate(datemap.date),
        endDate: datemap.endDate && partialDateFromDate(datemap.endDate),
        // If available from decription, add usage days as a field
        usageDays: currentDateData[0]?.description?.[3]
          ? (currentDateData[0]?.description?.[3].values as string | undefined)
          : undefined,
      },
      ...dataObj,
    };
  });
  return trData;
};

type TValueTypes = string | number | PartialDate;

const FormattedValue = ({ name, value }: { name: string; value?: TValueTypes }): React.JSX.Element => (
  <React.Fragment>
    {exists(value) ? (
      name === 'startDateOfPeriodUnderReview' && isPartialDate(value) ? (
        formatPartialDate(value)
      ) : name === 'targetVentilationAchievedInPlusMinus10PercentPrecision' ? (
        fm(`treatment.opts.yesNoUnknown.${value}`)
      ) : (
        <span style={{ fontWeight: 600 }}>
          {value}{' '}
          {['day', 'inhalationsPerMinute'].includes(defaultPapTreatmentResponseUnits[name])
            ? fm(`treatment.papTherapy.${defaultPapTreatmentResponseUnits[name]}`)
            : (defaultPapTreatmentResponseUnits[name] ?? '')}
        </span>
      )
    ) : (
      '-'
    )}
  </React.Fragment>
);

const ResMedTreatmentResponses = ({ treatmentResponses = [] }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const [tableTimeframe, setTableTimeframe] = React.useState<'daily' | 'weekly' | 'monthly' | '6months' | '12months'>(
    'daily',
  );
  /** Used to determine whether list or table view is active */
  const [activeView, setActiveView] = React.useState<'list' | 'table'>('list');

  const [allAggregatedData, setAllAggregatedData] = React.useState<TAggregatedTreatmentResponseDataPoints>([]);

  const aggregateData = () => {
    if (tableTimeframe === 'daily') return;
    const thisAgData: TAggregatedTreatmentResponseDataPoints = [];

    resMedFields.forEach(fld => {
      const resMedAggregateData: Array<{ date: Date; value: number; id: string; title: string }> = [];

      treatmentResponses.forEach(rtr => {
        if (!isPartialDate(rtr.date)) return;
        const value = rtr[fld as keyof IPapTreatmentResponse];
        if (typeof value === 'number') {
          resMedAggregateData.push({
            date: dateFromPartial(rtr.date),
            value: value,
            id: 'resMedPap',
            title: `${rtr.patientHoursOfUsePerDay}`,
          });
        }
      });
      const dataPoints = aggregateResmedDataByPeriod(
        resMedAggregateData,
        tableTimeframe,
        fm,
        '',
        fld === 'patientHoursOfUsePerDay',
      );
      dataPoints.forEach(dp => {
        thisAgData.push({
          field: fld as keyof IPapTreatmentResponse,
          date: dp.date,
          endDate: dp.endDate,
          alternateDate: dp.alternativeDateString,
          value: dp.value,
          description: Array.isArray(dp.description) ? dp.description : undefined,
        });
      });
    });
    setAllAggregatedData(thisAgData);
  };

  React.useEffect(() => {
    if (tableTimeframe === 'daily') {
      setAllAggregatedData([]);
    } else aggregateData();
  }, [tableTimeframe]);

  const dates = treatmentResponses
    ?.map(treatmentResponse => treatmentResponse.date)
    .filter(date => isPartialDate(date)) as PartialDate[];

  const [viewedData, setViewedData] = React.useState<Array<Partial<IPapTreatmentResponse>>>([]);

  React.useEffect(() => {
    setViewedData(
      tableTimeframe === 'daily' || activeView === 'list'
        ? treatmentResponses
        : makeTableTimeframeData(allAggregatedData),
    );
  }, [allAggregatedData, activeView]);

  // Track page width and breakpoints
  const md = useMediaQuery((theme: Theme) => theme.breakpoints.between(0, 1401));

  const tableFields = ['patientHoursOfUsePerDay', ...(tableTimeframe !== 'daily' ? ['usageDays'] : [])].concat(
    configFields,
  );

  return (
    <EventViewer
      name="treatmentResponses"
      formData={{
        onChange: () => '',
        document: { treatmentResponses: viewedData.toSorted((a, b) => sortPartialDate(a.date, b.date)) },
      }}
      dateFieldName="date"
      content={(d: IPapTreatmentResponse): React.JSX.Element => {
        return (
          <React.Fragment key={d.id}>
            {resMedFields.map((field, index) => {
              const value = d[field as keyof IPapTreatmentResponse];
              return exists(value) ? (
                <StepperHeaderValuePair
                  key={`${field}${index}`}
                  header={fm(`treatment${field === 'startDateOfPeriodUnderReview' ? '.' : '.papTherapy.'}${field}`)}
                  value={<FormattedValue name={field} value={value} />}
                />
              ) : null;
            })}
          </React.Fragment>
        );
      }}
      tableContent={(events: IPapTreatmentResponse[]) => {
        return (
          <React.Fragment>
            {tableFields.map((field, index, arr) => (
              <Container
                key={index}
                alignItems="baseline"
                style={{
                  marginBottom: index < arr.length - 1 ? '1rem' : '4.5rem',
                }}
              >
                <Item xs={4} style={{ paddingLeft: '2rem' }}>
                  {field === 'usageDays'
                    ? fm('graph.resMed.papUsageDays')
                    : fm(`treatment${field === 'startDateOfPeriodUnderReview' ? '.' : '.papTherapy.'}${field}`)}
                  {field !== 'usageDays' && (
                    <span>
                      {' '}
                      (
                      {['day', 'inhalationsPerMinute'].includes(defaultPapTreatmentResponseUnits[field])
                        ? fm(`treatment.papTherapy.${defaultPapTreatmentResponseUnits[field]}`)
                        : (defaultPapTreatmentResponseUnits[field] ?? '')}
                      )
                    </span>
                  )}
                </Item>
                {events.map((e: IPapTreatmentResponse, i: number, arr) => {
                  const value = e[field as keyof IPapTreatmentResponse];

                  return (
                    <Item key={i} xs={index === 0 && i === arr.length - 1 ? 1 : 2}>
                      <span style={{ fontWeight: 600 }}>
                        {typeof value === 'number' ? value.toFixed(1) : typeof value === 'string' ? value : '-'}
                      </span>
                    </Item>
                  );
                })}
              </Container>
            ))}
          </React.Fragment>
        );
      }}
      tableHeaders={(d: IPapTreatmentResponse & { endDate?: PartialDate }): React.JSX.Element => {
        return (
          <>
            {isPartialDate(d.endDate) ? (
              <>
                <div style={{ color: colors.darkGray, fontSize: md ? '1.2rem' : '1.4rem', lineHeight: '1rem' }}>
                  {formatPartialDate(d.date)}
                  {` -> `}
                </div>
                <div>{formatPartialDate(d.endDate)}</div>
              </>
            ) : isPartialDate(d.date) ? (
              <>{formatPartialDate(d.date)}</>
            ) : (
              'No date'
            )}
          </>
        );
      }}
      header="treatment.papTherapy.treatmentResponses"
      dateDefault={dates[0]}
      enabledDatePickerDates={dates}
      noEventsTextHeader="treatment.papTherapy.noTreatmentResponses"
      activeView={activeView}
      setActiveView={view => setActiveView(view)}
      tableContentFilter={{
        width: 4,
        input: (
          <InputHandler
            editing
            type="Select"
            name="timeframe"
            options={['daily', 'weekly', 'monthly', '6months', '12months']}
            optionFormatter={o => fm('graph.resMed.tabs.' + o)}
            formData={{
              document: { timeframe: tableTimeframe },
              onChange: values => {
                const name = Object.keys(values)[0];
                const value = values[name];
                setTableTimeframe(value as 'daily' | 'weekly' | 'monthly' | '6months' | '12months');
              },
            }}
          />
        ),
      }}
    />
  );
};

interface IOwnProps {
  treatmentResponses?: Array<IPapTreatmentResponse>;
}

export default ResMedTreatmentResponses;
